import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  CheckCircle, Clock, MapPin, Music, Users, 
  Loader2, ChevronRight, Shield, Filter, Phone
} from "lucide-react";
import { format, isToday, isTomorrow, startOfDay, parseISO, differenceInMinutes } from "date-fns";
import { logActivity } from "@/lib/activityLogger";
import { useUserRoles } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { detectRegionFromAddress } from "@/lib/regionDetection";
import { getTechSpecForInstrument } from "@/lib/techRiderData";
import type { UpcomingEvent, EventCheckIn, MusicianCheckIn, VendorContact, MusicianRosterInfo } from "@/components/ops/types";
import { VendorContactsCard } from "@/components/ops/VendorContactsCard";
import { MusicianWorkflowCard } from "@/components/ops/MusicianWorkflowCard";
import { EventTasksCard } from "@/components/ops/EventTasksCard";
import { DebriefCompleteCard } from "@/components/ops/DebriefCompleteCard";

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function OpsApp() {
  const { data: roles = [], isLoading: roleLoading } = useUserRoles();
  const isAdmin = roles.includes('admin');
  const navigate = useNavigate();
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<UpcomingEvent | null>(null);
  const [briefingNotes, setBriefingNotes] = useState("");
  const [setupNotes, setSetupNotes] = useState("");

  // Persist draft notes to sessionStorage
  const persistDraft = useCallback((checkInId: string, field: string, value: string) => {
    const key = `ops_draft_${checkInId}`;
    const existing = JSON.parse(sessionStorage.getItem(key) || "{}");
    sessionStorage.setItem(key, JSON.stringify({ ...existing, [field]: value }));
  }, []);

  const loadDraft = useCallback((checkInId: string) => {
    return JSON.parse(sessionStorage.getItem(`ops_draft_${checkInId}`) || "{}");
  }, []);

  const clearDraft = useCallback((checkInId: string) => {
    sessionStorage.removeItem(`ops_draft_${checkInId}`);
  }, []);
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [saving, setSaving] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState("");
  const [sendingHelp, setSendingHelp] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isAdmin) navigate("/auth", { state: { returnTo: "ops" } });
  }, [isAdmin, roleLoading, navigate]);

  useEffect(() => {
    if (isAdmin) fetchUpcomingEvents();
  }, [isAdmin]);

  const fetchUpcomingEvents = async () => {
    setLoading(true);
    try {
      const today = startOfDay(new Date());
      const { data: checkIns, error } = await supabase
        .from("event_check_ins")
        .select("*")
        .gte("event_date", format(today, "yyyy-MM-dd"))
        .order("event_date", { ascending: true });

      if (error) throw error;
      if (!checkIns?.length) { setEvents([]); setLoading(false); return; }

      const inquiryIds = [...new Set(checkIns.map(c => c.inquiry_id))];
      const checkInIds = checkIns.map(c => c.id);

      const [inquiriesRes, musiciansRes, vendorsRes] = await Promise.all([
        supabase.from("contact_submissions").select("id, name, occasion, venue_address, event_start_time, event_end_time").in("id", inquiryIds),
        supabase.from("musician_check_ins").select("*").in("event_check_in_id", checkInIds),
        supabase.from("vendor_contacts").select("id, name, category, phone, email, company, inquiry_id, vendor_rating, vendor_review_notes").in("inquiry_id", inquiryIds),
      ]);

      // Fetch roster info and event counts for musicians
      const musicianIds = (musiciansRes.data || []).map(m => m.musician_id).filter(Boolean) as string[];
      let rosterMap: Record<string, MusicianRosterInfo> = {};
      if (musicianIds.length > 0) {
        const [rosterRes, countsRes] = await Promise.all([
          supabase.from("musicians").select("id, composite_score, tier").in("id", musicianIds),
          supabase.from("musician_check_ins").select("musician_id").not("wrapped_up_at", "is", null).in("musician_id", musicianIds),
        ]);
        // Count completed events per musician
        const countMap: Record<string, number> = {};
        (countsRes.data || []).forEach(row => {
          const mid = (row as any).musician_id;
          if (mid) countMap[mid] = (countMap[mid] || 0) + 1;
        });
        (rosterRes.data || []).forEach(r => {
          rosterMap[r.id] = { ...r, eventCount: countMap[r.id] || 0 };
        });
      }

      const inquiryMap = new Map((inquiriesRes.data || []).map(i => [i.id, i]));
      const musicianMap = new Map<string, MusicianCheckIn[]>();
      (musiciansRes.data || []).forEach(m => {
        const arr = musicianMap.get(m.event_check_in_id) || [];
        arr.push(m as MusicianCheckIn);
        musicianMap.set(m.event_check_in_id, arr);
      });
      const vendorsByInquiry = new Map<string, VendorContact[]>();
      (vendorsRes.data || []).forEach(v => {
        const key = (v as any).inquiry_id || "";
        const arr = vendorsByInquiry.get(key) || [];
        arr.push(v as VendorContact);
        vendorsByInquiry.set(key, arr);
      });

      const mapped: UpcomingEvent[] = checkIns.map(c => ({
        checkIn: c as unknown as EventCheckIn,
        inquiry: inquiryMap.get(c.inquiry_id) || { id: c.inquiry_id, name: "Unknown", occasion: null, venue_address: "", event_start_time: "", event_end_time: "" },
        musicians: musicianMap.get(c.id) || [],
        vendorContacts: vendorsByInquiry.get(c.inquiry_id) || [],
        musicianRoster: rosterMap,
      }));

      setEvents(mapped);
      if (selectedEvent) {
        const updated = mapped.find(e => e.checkIn.id === selectedEvent.checkIn.id);
        if (updated) setSelectedEvent(updated);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  // Optimistic update helpers
  const updateSelectedMusician = useCallback((musicianId: string, updates: Partial<MusicianCheckIn>) => {
    setSelectedEvent(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        musicians: prev.musicians.map(m => m.id === musicianId ? { ...m, ...updates } : m),
      };
    });
  }, []);

  const updateSelectedVendor = useCallback((vendorId: string, updates: Partial<VendorContact>) => {
    setSelectedEvent(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        vendorContacts: prev.vendorContacts.map(v => v.id === vendorId ? { ...v, ...updates } : v),
      };
    });
  }, []);

  const updateSelectedCheckIn = useCallback((updates: Partial<EventCheckIn>) => {
    setSelectedEvent(prev => {
      if (!prev) return prev;
      return { ...prev, checkIn: { ...prev.checkIn, ...updates } };
    });
  }, []);

  const handleCheckIn = async (event: UpcomingEvent) => {
    setCheckingIn(true);
    try {
      // Time gate: must be within 2 hours of event start
      const eventDateTime = parseISO(`${event.checkIn.event_date}T${event.inquiry.event_start_time}`);
      const minutesUntil = differenceInMinutes(eventDateTime, new Date());
      if (minutesUntil > 120) {
        toast.error(`Too early to check in. Event starts in ${Math.round(minutesUntil / 60)} hours.`);
        setCheckingIn(false);
        return;
      }

      // Geolocation gate
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      }).catch(() => null);

      if (!pos) {
        toast.error("Location access required to check in. Please enable location services.");
        setCheckingIn(false);
        return;
      }

      // Geocode venue
      const { data: geoData } = await supabase.functions.invoke("geocode-venue", {
        body: { address: event.inquiry.venue_address },
      });

      if (geoData?.lat && geoData?.lng) {
        const dist = haversineDistance(pos.coords.latitude, pos.coords.longitude, geoData.lat, geoData.lng);
        if (dist > 1) {
          toast.error(`You're ${dist.toFixed(1)} miles from the venue. Must be within 1 mile to check in.`);
          setCheckingIn(false);
          return;
        }
      }

      const now = new Date().toISOString();
      updateSelectedCheckIn({ checked_in_at: now, status: "checked_in" });
      const { error } = await supabase
        .from("event_check_ins")
        .update({ checked_in_at: now, status: "checked_in" })
        .eq("id", event.checkIn.id);
      if (error) throw error;
      toast.success("Checked in!");
    } catch (err: any) {
      toast.error(err.message || "Check-in failed");
      fetchUpcomingEvents(); // revert on error
    } finally {
      setCheckingIn(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const updates = { setup_verified: true, setup_verified_at: now, setup_notes: setupNotes || null, briefing_completed: true, briefing_notes: briefingNotes || null };
      updateSelectedCheckIn(updates as any);
      const { error } = await supabase.from("event_check_ins").update(updates).eq("id", selectedEvent.checkIn.id);
      if (error) throw error;
      clearDraft(selectedEvent.checkIn.id);
      toast.success("Setup verified & briefing complete");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleHelpRequest = async () => {
    if (!selectedEvent || !helpMessage.trim()) return;
    setSendingHelp(true);
    try {
      const ev = selectedEvent;
      const inquiryId = ev.inquiry.id;

      // Flag inquiry as stress point
      await supabase.from("contact_submissions").update({ has_incident: true } as any).eq("id", inquiryId);
      await supabase.from("event_check_ins").update({ has_incident: true }).eq("id", ev.checkIn.id);

      // Log activity
      await logActivity({
        inquiryId,
        action: "help_requested",
        description: `🆘 Live help requested by RM: ${helpMessage.slice(0, 80)}`,
        metadata: { event_check_in_id: ev.checkIn.id, event_date: ev.checkIn.event_date, message: helpMessage },
      });

      // Insert urgent help email into CRM thread
      const bodyHtml = `
        <div style="font-family:sans-serif;max-width:600px">
          <h2 style="color:#dc2626">🆘 Live Help Request — ${ev.inquiry.name}</h2>
          <p style="background:#fef2f2;padding:12px;border-radius:8px;border:1px solid #fecaca">
            <strong>RM needs immediate assistance:</strong><br/>
            ${helpMessage.replace(/\n/g, "<br/>")}
          </p>
          <table style="font-size:12px;margin-top:12px;border-collapse:collapse">
            <tr><td style="padding:4px 8px;color:#666">Event</td><td style="padding:4px 8px">${ev.inquiry.name} — ${ev.inquiry.occasion || "N/A"}</td></tr>
            <tr><td style="padding:4px 8px;color:#666">Date</td><td style="padding:4px 8px">${format(new Date(ev.checkIn.event_date + "T00:00:00"), "MMM d, yyyy")}</td></tr>
            <tr><td style="padding:4px 8px;color:#666">Venue</td><td style="padding:4px 8px">${ev.inquiry.venue_address}</td></tr>
            <tr><td style="padding:4px 8px;color:#666">Musicians</td><td style="padding:4px 8px">${ev.musicians.map(m => `${m.musician_name} (${m.instrument})`).join(", ")}</td></tr>
          </table>
          <hr/>
          <p style="font-size:11px;color:#666">Sent from On-Site Ops at ${format(new Date(), "PPpp")}</p>
        </div>
      `;

      await supabase.from("inquiry_emails").insert({
        inquiry_id: inquiryId,
        recipient_email: "internal@theloststrings.com",
        subject: `🆘 LIVE HELP: ${ev.inquiry.name} — ${format(new Date(ev.checkIn.event_date + "T00:00:00"), "MMM d")}`,
        body_html: bodyHtml,
        status: "sent",
        template_used: "help_request",
      });

      setHelpDialogOpen(false);
      setHelpMessage("");
      toast.success("Help request sent — admin team notified!");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSendingHelp(false);
    }
  };

  const getDateLabel = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00");
    if (isToday(d)) return "Today";
    if (isTomorrow(d)) return "Tomorrow";
    return format(d, "EEE, MMM d");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500/15 text-green-700 border-green-500/30";
      case "checked_in": return "bg-primary/15 text-primary border-primary/30";
      default: return "bg-amber-500/15 text-amber-700 border-amber-500/30";
    }
  };

  if (roleLoading || loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageTransition>
    );
  }

  // ── Event Detail View ──
  if (selectedEvent) {
    const ev = selectedEvent;
    // Get unique instruments for tech rider
    const instrumentSpecs = ev.musicians.map(m => ({
      name: m.musician_name,
      instrument: m.instrument,
      spec: getTechSpecForInstrument(m.instrument),
    }));

    return (
      <PageTransition>
        <div className="min-h-screen bg-background p-4 max-w-lg mx-auto space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedEvent(null)} className="mb-2">
            &larr; Back
          </Button>
          <div>
            <h1 className="text-xl font-bold">{ev.inquiry.name}</h1>
            <p className="text-sm text-muted-foreground">{ev.inquiry.occasion} — {getDateLabel(ev.checkIn.event_date)}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" /> {ev.inquiry.venue_address}
            </p>
          </div>

          {/* Step 1: RM Check-In */}
          {!ev.checkIn.checked_in_at ? (
            <Card>
              <CardContent className="p-4">
                <Button onClick={() => handleCheckIn(ev)} className="w-full" size="lg" disabled={checkingIn}>
                  {checkingIn ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Shield className="h-5 w-5 mr-2" />}
                  Check In as Regional Manager
                </Button>
                <p className="text-[9px] text-muted-foreground text-center mt-1">Requires location within 1 mile & within 2hrs of event</p>
              </CardContent>
            </Card>
          ) : (
            <Badge className={getStatusColor("checked_in")}>
              <CheckCircle className="h-3 w-3 mr-1" />
              Checked in at {format(new Date(ev.checkIn.checked_in_at), "h:mm a")}
            </Badge>
          )}

          {/* Step 2: Vendor Contacts */}
          {ev.checkIn.checked_in_at && (
            <VendorContactsCard 
              contacts={ev.vendorContacts} 
              eventCompleted={!!ev.checkIn.completed_at}
              onUpdate={updateSelectedVendor}
            />
          )}

          {/* Step 3: Musicians */}
          {ev.checkIn.checked_in_at && (
            <MusicianWorkflowCard 
              musicians={ev.musicians} 
              roster={ev.musicianRoster}
              inquiryId={ev.inquiry.id}
              eventCheckInId={ev.checkIn.id}
              eventDate={ev.checkIn.event_date}
              onUpdate={updateSelectedMusician}
            />
          )}

          {/* Step 4: Setup & Briefing with Tech Rider */}
          {ev.checkIn.checked_in_at && !ev.checkIn.setup_verified && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Setup & Briefing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Tech Rider Summary */}
                {instrumentSpecs.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium">Tech Rider</label>
                    {instrumentSpecs.map((is, i) => (
                      <div key={i} className="p-2 bg-muted/50 rounded text-[10px] space-y-0.5">
                        <p className="font-medium">{is.name} — {is.instrument}</p>
                        <p>🎤 {is.spec.microphone.model}</p>
                        {is.spec.diBox?.required && <p>📦 DI: {is.spec.diBox.type}</p>}
                        <p>🔊 Monitor: {is.spec.monitor?.type}</p>
                        {is.spec.power?.required && <p>⚡ Power: {is.spec.power.outlets} outlets</p>}
                        {is.spec.stageNotes && <p className="text-muted-foreground">{is.spec.stageNotes}</p>}
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium">Briefing Notes</label>
                  <Textarea value={briefingNotes} onChange={e => { setBriefingNotes(e.target.value); persistDraft(ev.checkIn.id, "briefingNotes", e.target.value); }} placeholder="DJ name, AV contact, special instructions..." rows={2} />
                </div>
                <div>
                  <label className="text-xs font-medium">Setup Notes</label>
                  <Textarea value={setupNotes} onChange={e => { setSetupNotes(e.target.value); persistDraft(ev.checkIn.id, "setupNotes", e.target.value); }} placeholder="Stage position, power access, mic check details..." rows={2} />
                </div>
                <Button onClick={handleVerifySetup} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Verify Setup & Complete Briefing
                </Button>
              </CardContent>
            </Card>
          )}
          {ev.checkIn.setup_verified && (
            <Badge className="bg-green-500/15 text-green-700 border-green-500/30">
              <CheckCircle className="h-3 w-3 mr-1" /> Setup verified
            </Badge>
          )}

          {/* Step 5: Event Tasks */}
          {ev.checkIn.checked_in_at && (
            <EventTasksCard checkIn={ev.checkIn} onUpdateCheckIn={updateSelectedCheckIn} />
          )}

          {/* Step 6: Complete */}
          {ev.checkIn.checked_in_at && ev.checkIn.setup_verified && (
            <DebriefCompleteCard checkIn={ev.checkIn} musicians={ev.musicians} inquiryId={ev.inquiry.id} onRefresh={fetchUpcomingEvents} />
          )}

          {/* Help Line — always visible after check-in, not after completion */}
          {ev.checkIn.checked_in_at && !ev.checkIn.completed_at && (
            <Button
              variant="destructive"
              className="w-full animate-pulse"
              size="lg"
              onClick={() => setHelpDialogOpen(true)}
            >
              <Phone className="h-5 w-5 mr-2" />
              🆘 Help Line — Request Admin Support
            </Button>
          )}

          {/* Help Line Dialog */}
          <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <Phone className="h-5 w-5" /> 🆘 Help Line
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground">
                Describe what's happening. An admin will be alerted immediately in the CRM.
              </p>
              <Textarea
                placeholder="What do you need help with? (musician issue, equipment problem, venue concern...)"
                rows={4}
                value={helpMessage}
                onChange={e => setHelpMessage(e.target.value)}
                className="text-sm"
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setHelpDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={sendingHelp || helpMessage.trim().length < 5}
                  onClick={handleHelpRequest}
                >
                  {sendingHelp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                  Send Help Request
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </PageTransition>
    );
  }

  // ── Event List View ──
  return (
    <PageTransition>
      <div className="min-h-screen bg-background p-4 max-w-lg mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Music className="h-5 w-5 text-primary" />
            On-Site Ops
          </h1>
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
            Dashboard
          </Button>
        </div>

        {/* Region Filter */}
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {['all', 'East', 'Central', 'West'].map(r => (
            <Button
              key={r}
              variant={regionFilter === r ? "default" : "outline"}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setRegionFilter(r)}
            >
              {r === 'all' ? 'All Regions' : r}
            </Button>
          ))}
        </div>

        {(() => {
          const filteredEvents = regionFilter === 'all' 
            ? events 
            : events.filter(ev => detectRegionFromAddress(ev.inquiry.venue_address) === regionFilter);
          
          return filteredEvents.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-40" />
                <p className="text-sm text-muted-foreground">
                  {events.length === 0 ? 'No upcoming events' : `No events in ${regionFilter} region`}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredEvents.map(ev => {
              const region = detectRegionFromAddress(ev.inquiry.venue_address);
              return (
                <Card 
                  key={ev.checkIn.id} 
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => {
                    setSelectedEvent(ev);
                    const draft = loadDraft(ev.checkIn.id);
                    setBriefingNotes(draft.briefingNotes ?? ev.checkIn.briefing_notes ?? "");
                    setSetupNotes(draft.setupNotes ?? ev.checkIn.setup_notes ?? "");
                  }}
                >
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{getDateLabel(ev.checkIn.event_date)}</Badge>
                        {region && (
                          <Badge variant="outline" className={cn("text-[9px] h-4 px-1.5",
                            region === 'East' ? 'bg-blue-500/10 border-blue-500/30 text-blue-600' :
                            region === 'Central' ? 'bg-amber-500/10 border-amber-500/30 text-amber-600' :
                            'bg-green-500/10 border-green-500/30 text-green-600'
                          )}>
                            {region}
                          </Badge>
                        )}
                        <span className="font-medium text-sm">{ev.inquiry.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{ev.inquiry.occasion}</p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {ev.inquiry.venue_address}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={cn("text-[9px]", getStatusColor(ev.checkIn.status))}>
                          {ev.checkIn.status}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          <Users className="h-3 w-3 inline mr-0.5" />{ev.musicians.length} musicians
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              );
            })
          );
        })()}
      </div>
    </PageTransition>
  );
}
